import React, { memo, useMemo } from "react";
import {
  Box,
  SimpleGrid,
  Flex,
  Text,
  useColorMode,
} from "@chakra-ui/react";
import { TriangleUpIcon, TriangleDownIcon, MinusIcon } from "@chakra-ui/icons";
import { getCurrentColor } from "../../constants";
import {
  computePriceMetrics,
  formatCpr,
  periodLabel,
  buildPriceSummarySentence,
} from "../../utils/priceMetrics";

// Down = green (good for the buyer), Up = red, Flat = muted.
const POSITIVE = "#16A34A"; // price went down
const NEGATIVE = "#DC2626"; // price went up
const NEUTRAL = "#94A3B8";

const directionColor = (direction) =>
  direction === "down"
    ? POSITIVE
    : direction === "up"
      ? NEGATIVE
      : NEUTRAL;

const MetricCard = memo(function MetricCard({
  label,
  value,
  valueColor,
  colorMode,
}) {
  return (
    <Box
      bg={getCurrentColor(colorMode, "whiteC.100", "grayC.1700")}
      border="1px solid"
      borderColor={getCurrentColor(colorMode, "grayC.3400", "grayC.2400")}
      borderRadius="6px"
      px={{ base: "10px", md: "14px" }}
      py={{ base: "10px", md: "12px" }}
      textAlign="center"
    >
      <Text
        fontSize={{ base: "10px", md: "xs" }}
        fontWeight="medium"
        textTransform="uppercase"
        letterSpacing="0.04em"
        color={NEUTRAL}
        mb="4px"
        noOfLines={1}
      >
        {label}
      </Text>
      <Text
        fontSize={{ base: "sm", md: "lg" }}
        fontWeight="bold"
        color={valueColor ?? getCurrentColor(colorMode, "#212936", "white")}
        noOfLines={1}
      >
        {value}
      </Text>
    </Box>
  );
});

const PriceHistoryStats = memo(function PriceHistoryStats({
  history,
  period,
  label,
  offersOverride,
}) {
  const { colorMode } = useColorMode();

  const metrics = useMemo(() => computePriceMetrics(history), [history]);

  const summary = useMemo(
    () => buildPriceSummarySentence({ label, period, metrics }),
    [label, period, metrics],
  );

  if (!metrics) return null;

  const trendColor = directionColor(metrics.direction);
  const TrendIcon =
    metrics.direction === "down"
      ? TriangleDownIcon
      : metrics.direction === "up"
        ? TriangleUpIcon
        : MinusIcon;

  const trendText =
    metrics.direction === "flat"
      ? `Roughly flat over the ${periodLabel(period)}`
      : `Trending ${metrics.direction} ${Math.abs(
          metrics.changePct,
        ).toFixed(2)}% over the ${periodLabel(period)}`;

  const baseText = getCurrentColor(colorMode, "#212936", "white");

  // Product pages pass the live active-offer count so the card matches the
  // "N Active offers" shown on the page. Caliber pages omit it and fall back
  // to the latest value from the historical feed.
  const offersValue = Number.isFinite(offersOverride)
    ? offersOverride
    : (metrics.currentOffers ?? "N/A");

  return (
    <Box px={{ base: "9px", md: "25px" }} mb="14px">
      {/* Metric cards */}
      <SimpleGrid columns={{ base: 2, sm: 3, lg: 5 }} spacing="10px" mb="12px">
        <MetricCard
          label="Current"
          value={formatCpr(metrics.current, { withUnit: true })}
          colorMode={colorMode}
        />
        <MetricCard
          label="Lowest"
          value={formatCpr(metrics.lowest, { withUnit: true })}
          valueColor={POSITIVE}
          colorMode={colorMode}
        />
        <MetricCard
          label="Highest"
          value={formatCpr(metrics.highest, { withUnit: true })}
          valueColor={NEGATIVE}
          colorMode={colorMode}
        />
        <MetricCard
          label="Average"
          value={formatCpr(metrics.average, { withUnit: true })}
          colorMode={colorMode}
        />
        <MetricCard
          label="Offers"
          value={offersValue}
          colorMode={colorMode}
        />
      </SimpleGrid>

      {/* Trend banner */}
      <Flex
        align="center"
        gap="8px"
        bg={getCurrentColor(colorMode, "whiteC.100", "grayC.1700")}
        border="1px solid"
        borderColor={getCurrentColor(colorMode, "grayC.3400", "grayC.2400")}
        borderRadius="6px"
        px="14px"
        py="10px"
      >
        <TrendIcon w="12px" h="12px" color={trendColor} />
        <Text fontSize={{ base: "xs", md: "sm" }} fontWeight="semibold" color={trendColor}>
          {trendText}
        </Text>
        <Text
          fontSize={{ base: "xs", md: "sm" }}
          color={NEUTRAL}
          ml="auto"
          display={{ base: "none", md: "block" }}
        >
          {metrics.dataPoints} data points
        </Text>
      </Flex>

      {/* SEO-facing text version of the data (real DOM text, crawlable) */}
      <Text mt="10px" fontSize={{ base: "xs", md: "sm" }} color={baseText} lineHeight="1.6">
        {summary}
      </Text>
    </Box>
  );
});

export default PriceHistoryStats;
